import { useBoundStore } from "@/lib/stores/useBoundStore";
import { getRuntimeConfig } from "@/lib/runtimeConfig";
import { isPublicRoute } from "@/lib/helpers";

// URL getters — evaluate at call time so they pick up window.__VELYS_CONFIG__
// populated by RootLayout. Avoids Next.js build-time inlining of
// `process.env.NEXT_PUBLIC_*` that would freeze values into the client bundle.
export const getAssetsUrl = () => getRuntimeConfig().assetsUrl;
export const getFilesUrl = () => getRuntimeConfig().filesUrl;
export const getAssessmentsUrl = () => getRuntimeConfig().assessmentsUrl;
export const getCareplansUrl = () => getRuntimeConfig().careplansUrl;
export const getOrgsUrl = () => getRuntimeConfig().orgsUrl;
export const getUsersUrl = () => getRuntimeConfig().usersUrl;

// ---- HTTP (query/mutation) endpoints ----
//
// When the CloudFront AppSync proxy is enabled these are root-relative
// same-origin paths ("/graphql/users") instead of the AppSync hostname. That
// is what lets RUM attach an X-Ray trace header to GraphQL calls: the header
// is "non-simple", so cross-origin it would force a CORS preflight, and
// AppSync's CORS response is service-managed — a rejected preflight fails the
// request itself, not just the trace.
//
// The get*Url() getters above intentionally keep returning the RAW AppSync
// URL. appsync-realtime.ts derives the wss:// endpoint and the auth `host`
// from it by string replacement (and calls new URL() on it), so pointing
// those at a relative path would throw and break every subscription — and
// with it every upload, since the upload flows await subscription start_ack
// before the S3 PUT.
//
// Empty proxy path => fall back to the raw URL. That keeps local dev working
// (there is no CloudFront in front of `yarn dev`) and makes the proxy a
// runtime kill switch needing no code change or rebuild.
const httpEndpoint = (proxiedPath: string, rawUrl: string): string =>
  proxiedPath || rawUrl;

export const getAssetsGqlUrl = () => {
  const c = getRuntimeConfig();
  return httpEndpoint(c.assetsHttpUrl, c.assetsUrl);
};
export const getFilesGqlUrl = () => {
  const c = getRuntimeConfig();
  return httpEndpoint(c.filesHttpUrl, c.filesUrl);
};
export const getAssessmentsGqlUrl = () => {
  const c = getRuntimeConfig();
  return httpEndpoint(c.assessmentsHttpUrl, c.assessmentsUrl);
};
export const getCareplansGqlUrl = () => {
  const c = getRuntimeConfig();
  return httpEndpoint(c.careplansHttpUrl, c.careplansUrl);
};
export const getOrgsGqlUrl = () => {
  const c = getRuntimeConfig();
  return httpEndpoint(c.orgsHttpUrl, c.orgsUrl);
};
export const getUsersGqlUrl = () => {
  const c = getRuntimeConfig();
  return httpEndpoint(c.usersHttpUrl, c.usersUrl);
};

export class AppSyncError extends Error {
  errorType?: string;
  constructor(message: string, errorType?: string) {
    super(message);
    this.name = "AppSyncError";
    this.errorType = errorType;
  }
}

async function appsyncFetch<T>(
  url: string,
  token: string,
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify({ query, variables }),
  });

  if (res.status === 401) {
    // Don't force-logout on public routes (e.g. /legal) — see velys-api.ts.
    if (!isPublicRoute()) {
      useBoundStore.getState().signOutRedirect();
    }
    throw new Error("Unauthorized");
  }

  const json = await res.json();

  if (json.errors) {
    const errs = json.errors as { message: string; errorType?: string }[];
    const message = errs
      .map((e) => (e.errorType ? `${e.errorType}: ${e.message}` : e.message))
      .join(", ");
    throw new AppSyncError(message, errs[0]?.errorType);
  }
  return json.data;
}

export const assetsGql = <T>(
  token: string,
  query: string,
  variables?: Record<string, unknown>,
) => appsyncFetch<T>(getAssetsGqlUrl(), token, query, variables);
export const assessmentsGql = <T>(
  token: string,
  query: string,
  variables?: Record<string, unknown>,
) => appsyncFetch<T>(getAssessmentsGqlUrl(), token, query, variables);
export const careplansGql = <T>(
  token: string,
  query: string,
  variables?: Record<string, unknown>,
) => appsyncFetch<T>(getCareplansGqlUrl(), token, query, variables);
export const filesGql = <T>(
  token: string,
  query: string,
  variables?: Record<string, unknown>,
) => appsyncFetch<T>(getFilesGqlUrl(), token, query, variables);
export const orgsGql = <T>(
  token: string,
  query: string,
  variables?: Record<string, unknown>,
) => appsyncFetch<T>(getOrgsGqlUrl(), token, query, variables);
export const usersGql = <T>(
  token: string,
  query: string,
  variables?: Record<string, unknown>,
) => appsyncFetch<T>(getUsersGqlUrl(), token, query, variables);
